import React from 'react';
import {alpha, createTheme} from '@material-ui/core/styles';
import {ThemeProvider as ThemeProviderUI} from '@material-ui/styles';
import {connect} from 'react-redux'
import appConfig from './theme.config.json'

const baseTheme = (colors) => createTheme({
    palette: {
        primary: {
            main: colors['color-primary']
        },
        secondary: {
            main: colors['color-secondary']
        },
        error: {
            main: colors['color-red']
        },
        warning: {
            main: colors['color-yellow']
        },
        info: {
            main: colors['color-blue']
        },
        success: {
            main: colors['color-green']
        },
    }
});


const theme = ({fontSize, colors, baseTheme}) => createTheme({
    typography: {
        fontFamily: appConfig['font-family'],
        fontSize: fontSize,
        cutLines: (num) => ({
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            '-webkit-box-orient': 'vertical',
            overflow: 'hidden',
            lineClamp: num,
        })
    },
    palette: {
        primary: {
            ...baseTheme.palette.primary
        },
        secondary: {
            ...baseTheme.palette.secondary
        },
        error: {
            ...baseTheme.palette.error
        },
        warning: {
            ...baseTheme.palette.warning
        },
        info: {
            ...baseTheme.palette.info
        },
        success: {
            ...baseTheme.palette.success
        },

        action: {
            active: colors['active'],
            disabled: colors['disabled'],
            disabledBackground: colors['disabled-background']
        },


        common: {
            success: colors['color-green'],
            warning: colors['color-yellow'],
            error: colors['color-red'],
            info: colors['color-blue'],
            document: colors['document'],
            gray: colors['color-gray'],
            lightGreen: colors['color-light-green'],
            lightRed: colors['color-light-red'],
        },
        background: {
            default: colors['default-background'],
            paper: colors['paper-background'],
            greyBackground: colors['greyBackground-background'],
            appBarBackground: colors['appBar-background'],
        },
        border: {
            onlyContrast: colors?.border?.onlyContrast,
            dark: colors.border.dark
        },
        text: colors['text'],
        chips: colors['chips'],
    },
    shape: {
        borderRadius: appConfig['border-radius']
    },
    mixins: {
        toolbar: {
            minHeight: 64
        }
    },
    props: {
        MuiTooltip: {
            placement: 'top'
        }
    },
    overrides: {
        MuiAppBar: {
            colorDefault: {
                backgroundColor: baseTheme.palette.background.appBarBackground,
                color: colors.active
            },
            colorPrimary: {
                color: colors.active
            }
        },
        MuiAccordion: {
            root: {
                border: colors?.border?.onlyContrast ? `1px solid ${colors.border.onlyContrast}` : undefined
            }
        },
        MuiInput: {
            underline: {
                '&:before': {
                    borderBottomColor: colors.inputBorder,
                }
            }
        },
        MuiListItem: {
            root: {
                "&$selected": {
                    backgroundColor: baseTheme.palette.secondary.main,
                    '&:hover': {
                        backgroundColor: baseTheme.palette.secondary.light
                    }
                }
            },
            button: {
                '&:hover': {
                    backgroundColor: baseTheme.palette.secondary.light
                }
            }
        },
        MuiButton: {
            outlinedPrimary: {
                borderColor: baseTheme.palette.primary.main
            },
            containedPrimary: {
                color: colors.text.button
            },
            containedSecondary: {
                color: colors.text.button
            },
            textSecondary: {
                color: colors.active
            },
            outlinedSecondary: {
                color: colors.active,
            }
        },
        MuiRadio: {
            colorPrimary: {
                "&$checked": {
                    color: baseTheme.palette.text.primary
                }
            }
        },
        MuiToggleButton: {
            root: {
                borderColor: baseTheme.palette.primary.main,
                color: baseTheme.palette.primary.main + " !important",
                "&:hover": {
                    backgroundColor: alpha(baseTheme.palette.primary.main, 0.04),
                },
                "&$disabled": {
                    pointerEvents: "unset"
                },
                "&$selected": {
                    backgroundColor: baseTheme.palette.primary.main,
                    color: colors.text.button + " !important",
                    cursor: 'default',
                    "&:hover": {
                        backgroundColor: baseTheme.palette.primary.main,
                    }
                }
            }
        },
        MuiTooltip: {
            tooltip: {
                fontSize: '.9rem',
				lineHeight: '1.1rem',
				maxWidth: 400
            }
        },
        MuiTableCell: {
            head: {
                fontWeight: 700
            }
        },
        MuiPaginationItem: {
            textPrimary: {
                "&$selected": {
                    fontWeight: 700,
                    color: colors.text.button
                }
            }
        },
        MuiPickersModal: {
            dialogRoot: {
                border: colors?.border?.onlyContrast ? `1px solid ${colors.border.onlyContrast}` : undefined
            }
        },
        MuiInputAdornment: {
            positionEnd: {
                "& .MuiIconButton-root": {
                    color: baseTheme.palette.primary.main
                }
            }
        },
        MuiTreeItem: {
            root: {
                "&.Mui-selected > .MuiTreeItem-content": {
                    background: baseTheme.palette.primary.main,
                    color: baseTheme.palette.secondary.contrastText
                }
            },
            content: {
                padding: "8px 0",
                "&:hover": {
                    backgroundColor: baseTheme.palette.primary.main
                }
            },
            label: {
                "&:hover": {
                    backgroundColor: "unset"
                },
                paddingRight: "8px"
            },
            iconContainer: {
                paddingLeft: "8px"
            },

        },
        MuiChip: {
            deleteIcon: {
                color: baseTheme.palette.primary.main,
                '&:hover': {
                    color: baseTheme.palette.primary.dark
                }
            },
            outlined: {
                border: `1px solid ${baseTheme.palette.primary.main}`
            }
        }
    }
})

const enrich = (theme) => ({
    ...theme,
    components: {
        paper: {
            padding: theme.spacing(2),
        },
        navigation: {
            width: 300,
            item: {
                primary: {
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': {
                        backgroundColor: theme.palette.primary.light,
                    }
                }
            },
            margin: {
                left: theme.spacing(7) + 1
            },
            transitionProp: {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }
        },
        progressBar: {
            buttons: {
                width: 180
            }
        },
        transferList: {
            row: {
                height: 36
            }
        },
        popup: {
            criteria: {
                width: 800,
            },
            smallContainer: {
                width: 300,
            },
            buttons: {
                width: 120,
            }
        },
        icon: {
            fontSize: 100,
        },
        input: {
            search: {
                width: 400,
            },
            criteria: {
                width: 50
            },
            criteriaKey: {
                width: 190
            }
        },
        table: {
            lpColumn: {
                width: 16,
            },
            dateColumn: {
                width: 120,
            },
            dateTimeColumn: {
                width: 140,
            },
            ipColumn: {
                width: 100,
            },
            emailColumn: {
                width: 230,
            },
            buttonColumn: {
                width: 48,
                padding: '0 !important'
            }
        },
        stageBar: {
            topOffset: 136,
            topOffsetExpanded: 282
        }
    },
    transitions: {
        ...theme.transitions,
        default: {
            open: prop => theme.transitions.create(prop, {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            close: prop => theme.transitions.create(prop, {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        }
    },
    scrollSize: 17
})

const applayClientColors = (colors, clientColors) => {
    if (clientColors) {
        if (clientColors.basicColorLight && clientColors.basicColorLightDarken) {
            colors["color-primary"] = "#" + clientColors.basicColorLight;
            colors["color-primary-dark"] = "#" + clientColors.basicColorLightDarken;

        }
        if (clientColors.basicColorDark && clientColors.basicColorDarkDarken) {
            colors["color-secondary"] = "#" + clientColors.basicColorDark;
            colors["color-secondary-dark"] = "#" + clientColors.basicColorDarkDarken;
        }
    }
    return colors;
}

const enrichTheme = (isContrast, isLargeText, clientColors) => {
    const fontSize = isLargeText ? appConfig['large-font-size'] : undefined;
    const colors = isContrast ? appConfig.colors['contrast'] : applayClientColors(appConfig.colors['main'], clientColors);

    const baseThemeRes = baseTheme(colors);

    const overrideTheme = theme({fontSize, colors, baseTheme: baseThemeRes});
    const finalTheme = enrich(overrideTheme)

    if (process.env.NODE_ENV === 'development') {
    }
    return finalTheme;
}

function ThemeProvider(
    {
        children,
        contrast,
        largeText,
        clientColors
    }) {

    return (
        <ThemeProviderUI theme={enrichTheme(contrast, largeText, clientColors)}>
            {children}
        </ThemeProviderUI>
    )
}

export default connect((state) => ({
    contrast: state.store.contrast,
    largeText: state.store.largeText,
    clientColors: state.store.clientColors
}), {})(ThemeProvider);